import React, { FC, useCallback, useEffect, useState } from 'react';
import update from 'immutability-helper';
import { DraggableRow } from './DraggableRow';
import _ from 'lodash';
import { BlazeTableColDef } from '../BlazeTable.types';
import { useIsMount } from '../../../hooks/useIsMountHook';

export interface SortableTableBodyProps {
  items: any[];
  columnDef: BlazeTableColDef[];
  onDragFinished: (items: any[]) => void;
  onSelectionChanged: (ids: number[]) => void;
  isSelectAll: boolean;
  expandableComponent?: (item: any) => any;
  selectedItems?: number[] | null;
}

export const DraggableTableBody: FC<SortableTableBodyProps> = (props: SortableTableBodyProps) => {
  const [items, setItems] = useState(props.items);
  const [selectedItems, setSelectedItems] = useState([]);
  const isMount = useIsMount();

  /** In case items are being loaded from backend. */
  useEffect(() => {
    if (!isMount) {
      setItems(props.items);
    }
  }, [props.items]);

  useEffect(() => {
    if (!isMount) {
      props.onSelectionChanged(selectedItems);
    }
  }, [selectedItems]);

  /** If select all changes, reset previously selected items to keep consistency. */
  useEffect(() => {
    if (!isMount && props.isSelectAll === false) {
      setSelectedItems([]);
    }
  }, [props.isSelectAll]);

  const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
    setItems((prevItems: any[]) =>
      update(prevItems, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevItems[dragIndex]],
        ],
      }),
    );
  }, []);

  const updateSelection = (selectedItems: number[], selectedId: number) => {
    const index = _.findIndex(selectedItems, elId => elId === selectedId);
    const updatedSelection = _.cloneDeep(selectedItems);
    if (index === -1) {
      updatedSelection.push(selectedId);
    } else {
      updatedSelection.splice(index, 1);
    }
    return updatedSelection;
  };

  const onSelectItemHandler = (id: number) => {
    // Selection is handled outside
    if (!_.isNil(props.selectedItems)) {
      const updatedSelection = updateSelection(props.selectedItems, id);
      props.onSelectionChanged(updatedSelection);
    }
    // Local state selection
    else {
      const updatedSelection = updateSelection(selectedItems, id);
      setSelectedItems(updatedSelection);
    }
  };

  const isItemSelected = (item: any) => {
    let indexItemFound = false;

    // Selection is overwritten from outside
    if (props.selectedItems) {
      indexItemFound = _.findIndex(props.selectedItems, itemId => item.id === itemId) !== -1;
    }
    // Local state selection
    else {
      indexItemFound = _.findIndex(selectedItems, itemId => item.id === itemId) !== -1;
    }
    return props.isSelectAll || indexItemFound;
  };

  const renderItem = useCallback(
    (item: any, index: number) => (
      <DraggableRow
        isSelected={isItemSelected(item)}
        onMoveFinished={() => {
          props.onDragFinished(items);
        }}
        key={item.id}
        index={index}
        item={item}
        moveItem={moveItem}
        columnDef={props.columnDef}
        onSelect={onSelectItemHandler}
        expandableComponent={props.expandableComponent}
      />
    ),
    [items, selectedItems, props.selectedItems, props.isSelectAll],
  );

  return <div>{!_.isNil(items) ? items.map((item, i) => renderItem(item, i)) : undefined}</div>;
};
